import { useEffect, useRef } from "react";
import { Ticket, Compass, ArrowUpRight } from "lucide-react";
import { Reveal } from "./motion";

// Compact affiliate ticket card — picture + headline only. The GYG widgets are
// cross-origin iframes whose inner text can't be trimmed, so for spots where the
// full widget takes too much room we link straight to the tour page instead.
export const GygTicketCard = ({ title, img, url, testid = "gyg-ticket-card" }) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer sponsored"
    data-testid={testid}
    className="group flex items-center bg-white border border-zinc-200 shadow-brut rounded-xl overflow-hidden hover:-translate-y-0.5 hover:shadow-lg transition-all"
  >
    <img src={img} alt={title} loading="lazy" decoding="async" className="w-24 md:w-28 self-stretch object-cover shrink-0" />
    <span className="flex-1 px-4 py-3">
      <span className="flex items-center gap-1.5 font-body font-800 text-[10px] tracking-[0.2em] text-[#DF3226]">
        <Ticket size={10} /> AD · GETYOURGUIDE
      </span>
      <span className="block font-display font-900 tracking-tight text-base md:text-lg leading-tight mt-1 group-hover:text-[#DF3226] transition-colors">
        {title}
      </span>
    </span>
    <ArrowUpRight size={16} className="shrink-0 mr-4 text-black/30 group-hover:text-[#DF3226] transition-colors" />
  </a>
);

// Tivoli entry ticket — the compact card reused wherever Tivoli shows up.
export const TIVOLI_TICKET_URL =
  "https://www.getyourguide.dk/kobenhavn-l12/kobenhavn-entrebillet-til-tivoli-t72048/?partner_id=HIOKDT8&utm_medium=online_publisher";
export const TIVOLI_TICKET_IMG =
  "https://images.unsplash.com/photo-1646210201164-4cc951b59bbc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxjb3BlbmhhZ2VuJTIwdGl2b2xpfGVufDB8fHx8MTc4NzA1NzI2NHww&ixlib=rb-4.1.0&q=85";
export const GygTivoliCard = ({ testid }) => (
  <GygTicketCard testid={testid} title="Tivoli Gardens — entry ticket" img={TIVOLI_TICKET_IMG} url={TIVOLI_TICKET_URL} />
);

// GetYourGuide loader lives in <head> (public/index.html). On a SPA route change
// a widget div can mount after the loader has already scanned the DOM, so we
// re-inject the loader once if any widget inside `ref` hasn't rendered yet.
const useGygInit = (ref) => {
  useEffect(() => {
    const t = setTimeout(() => {
      const widgets = ref.current?.querySelectorAll("[data-gyg-widget]") || [];
      const needsInit = Array.from(widgets).some((el) => el.children.length === 0);
      if (needsInit) {
        const s = document.createElement("script");
        s.src = "https://widget.getyourguide.com/dist/pa.umd.production.min.js";
        s.async = true;
        s.defer = true;
        s.setAttribute("data-gyg-partner-id", "HIOKDT8");
        document.body.appendChild(s);
      }
    }, 1200);
    return () => clearTimeout(t);
  }, [ref]);
};

// Wraps a GYG widget: the "Powered by … Join our travel affiliate program" footer
// lives inside the cross-origin iframe and can't be edited — so we cover the
// iframe's bottom strip (a consistent 48px across widget types and breakpoints)
// with our own caption bar instead.
const AdShell = ({ widgetRef, className = "", children }) => (
  <div ref={widgetRef} className={className}>
    <div className="relative">
      {children}
      <div className="absolute inset-x-0 bottom-0 h-[54px] bg-white border-t border-dotted border-zinc-300/70 flex items-end pb-1.5">
        <p className="font-body text-[11px] text-black/40">This is an ad · Powered by GetYourGuide</p>
      </div>
    </div>
  </div>
);

// Availability/booking widget for a single tour (horizontal booking bar etc.).
export const GygAvailability = ({ tourId, variant = "horizontal", currency = "EUR" }) => {
  const ref = useRef(null);
  useGygInit(ref);
  if (!tourId) return null;
  return (
    <AdShell widgetRef={ref}>
      <div
        data-gyg-href="https://widget.getyourguide.com/default/availability.frame"
        data-gyg-tour-id={tourId}
        data-gyg-locale-code="en-US"
        data-gyg-currency={currency}
        data-gyg-widget="availability"
        data-gyg-variant={variant}
        data-gyg-partner-id="HIOKDT8"
      >
        <span>
          Powered by{" "}
          <a target="_blank" rel="sponsored noopener noreferrer" href="https://www.getyourguide.com/copenhagen-l12/">
            GetYourGuide
          </a>
        </span>
      </div>
    </AdShell>
  );
};

// Full city widget — the whole Copenhagen activities grid (user-provided embed).
export const GygCity = ({ locale = "en-US" }) => {
  const ref = useRef(null);
  useGygInit(ref);
  return (
    <AdShell widgetRef={ref}>
      <div
        data-gyg-href="https://widget.getyourguide.com/default/city.frame"
        data-gyg-location-id="3819"
        data-gyg-locale-code={locale}
        data-gyg-widget="city"
        data-gyg-partner-id="HIOKDT8"
      ></div>
    </AdShell>
  );
};

// Reusable activities widget for a specific set of tour IDs (used on area pages).
export const GygActivities = ({ tourIds, q, numberOfItems = 3 }) => {
  const ref = useRef(null);
  useGygInit(ref);
  if (!tourIds && !q) return null;
  return (
    <AdShell widgetRef={ref}>
      <div
        data-gyg-href="https://widget.getyourguide.com/default/activities.frame"
        data-gyg-locale-code="en-US"
        data-gyg-widget="activities"
        data-gyg-number-of-items={String(numberOfItems)}
        {...(tourIds ? { "data-gyg-tour-ids": tourIds } : { "data-gyg-q": q })}
        data-gyg-partner-id="HIOKDT8"
      ></div>
    </AdShell>
  );
};

// Standard "BOOK A TOUR" band for the bottom of guide pages — a single bookable
// availability bar in the shared cream section styling. `flush` drops the border
// and top padding when it directly follows a same-coloured section (e.g. the FAQ).
export const GygTourBand = ({ tourId, label = "BOOK A TOUR", flush = false }) => (
  <section
    data-testid="gyg-tour-band"
    className={flush ? "pb-12 md:pb-16 bg-[#F9F8F6]" : "py-12 md:py-16 bg-[#F9F8F6] border-t border-dotted border-zinc-300"}
  >
    <div className="max-w-[1400px] mx-auto px-5 md:px-10">
      <div className="flex items-center gap-2 mb-6">
        <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">{label}</p>
      </div>
      <GygAvailability tourId={tourId} variant="horizontal" />
    </div>
  </section>
);

// General Copenhagen ad — the city-wide GetYourGuide browser (same widget as the
// homepage bottom) shown on pages that have no other GetYourGuide placement.
export const GygGeneralAd = () => {
  const ref = useRef(null);
  useGygInit(ref);
  return (
    <section data-testid="gyg-general-ad" className="py-12 md:py-16 bg-[#F9F8F6] border-t border-dotted border-zinc-300">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex items-center justify-center gap-2 mb-6">
          <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">TOURS &amp; TICKETS</p>
        </div>
        <AdShell widgetRef={ref} className="max-w-2xl mx-auto">
          <div
            data-gyg-href="https://widget.getyourguide.com/default/city.frame"
            data-gyg-location-id="12"
            data-gyg-locale-code="en-US"
            data-gyg-widget="city"
            data-gyg-partner-id="HIOKDT8"
          ></div>
        </AdShell>
      </div>
    </section>
  );
};

// Hand-picked, bookable tours (activities widget) — placed under "What's on".
export const GetYourGuide = () => {
  return (
    <section id="tours" data-testid="getyourguide-tours" className="py-12 md:py-24 bg-[#F9F8F6] border-y border-dotted border-zinc-300">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal>
          <div className="flex items-center gap-2 mb-4">
            <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">TOURS &amp; TICKETS</p>
          </div>
          <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
            Walk the city —<br />
            <span className="font-editorial italic font-600">with a local guide</span>
          </h2>
          <p className="font-body text-base md:text-lg text-black/70 max-w-2xl mt-6">
            Our current favourite guided experience — bookable in a couple of taps, with free
            cancellation.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-10">
            <GygAvailability tourId="689802" variant="horizontal" />
          </div>
        </Reveal>
      </div>
    </section>
  );
};

// The larger "city" browse widget — placed at the very bottom, after the FAQ.
export const GetYourGuideCity = () => {
  const ref = useRef(null);
  useGygInit(ref);

  return (
    <section id="explore-more" data-testid="getyourguide-city" className="py-12 md:py-24 bg-white border-t border-dotted border-zinc-300">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal>
          <div className="flex items-center gap-2 mb-4">
            <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">EXPLORE MORE</p>
          </div>
          <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
            Everything to do in{" "}
            <span className="font-editorial italic font-600">Copenhagen</span>
          </h2>
        </Reveal>

        <Reveal delay={0.05}>
          <AdShell widgetRef={ref} className="mt-10">
            <div
              data-gyg-href="https://widget.getyourguide.com/default/city.frame"
              data-gyg-location-id="12"
              data-gyg-locale-code="en-US"
              data-gyg-widget="city"
              data-gyg-partner-id="HIOKDT8"
            ></div>
          </AdShell>
        </Reveal>
      </div>
    </section>
  );
};
